import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Cpu, Settings2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useActiveDevice } from '../../hooks/useActuators';
import { Dot } from '../UI/rt';

type DeviceStatus = 'online' | 'offline' | 'error' | 'maintenance' | 'unknown';

const statusToTone = (status?: DeviceStatus) => {
  switch (status) {
    case 'online':
      return 'success' as const;
    case 'error':
      return 'danger' as const;
    case 'maintenance':
      return 'warn' as const;
    default:
      return 'idle' as const;
  }
};

interface DevicePickerProps {
  variant?: 'topbar' | 'drawer';
  className?: string;
}

export const DevicePicker: React.FC<DevicePickerProps> = ({
  variant = 'topbar',
  className,
}) => {
  const navigate = useNavigate();
  const { activeDeviceId, setDevice, devices, isLoading } = useActiveDevice();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const activeDevice = devices?.find(
    (d) => String(d.id || d.device_id) === activeDeviceId,
  );

  if (!devices || devices.length === 0) {
    return null;
  }

  const buttonLabel = activeDevice?.name || activeDevice?.device_id || '—';

  return (
    <div ref={containerRef} className={clsx('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={isLoading}
        className={clsx(
          // min-w-0 + max-w-full: v uzkem hornim pruhu se musi umet smrsknout,
          // jinak vytlaci sousedni prvky (hamburger) mimo obrazovku.
          'inline-flex items-center gap-2 h-9 rounded-[10px] text-[13px] font-medium transition-colors rt-focus-visible min-w-0 max-w-full',
          variant === 'topbar'
            ? 'px-2.5 bg-[var(--rt-ink-100)]/60 dark:bg-white/5 text-[var(--rt-ink-700)] dark:text-zinc-300 hover:bg-[var(--rt-ink-100)] dark:hover:bg-white/10'
            : 'w-full justify-between px-3 border rt-divider bg-transparent',
        )}
      >
        <Cpu size={14} className="shrink-0 text-[var(--rt-ink-500)] dark:text-zinc-400" />
        <Dot tone={statusToTone(activeDevice?.status as DeviceStatus)} />
        {/* Na uzkych displejich staci kratsi nazev — text se orizne, ne roztahne pruh. */}
        <span className="max-w-[88px] sm:max-w-[140px] truncate">{buttonLabel}</span>
        <ChevronDown
          size={12}
          className={clsx('opacity-60 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Vybrat aktivní zařízení"
          className="absolute right-0 mt-1 w-64 rt-card overflow-hidden p-1 z-40"
        >
          <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--rt-ink-500)] dark:text-zinc-500">
            Aktivní zařízení
          </div>
          <div className="max-h-72 overflow-y-auto">
            {devices.map((d) => {
              const id = String(d.id || d.device_id);
              const isActive = id === activeDeviceId;
              return (
                <button
                  key={id}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => {
                    setDevice(id);
                    setOpen(false);
                  }}
                  className={clsx(
                    'w-full flex items-center gap-2.5 px-3 h-10 rounded-md text-left text-[13px] transition-colors',
                    isActive
                      ? 'bg-[var(--rt-brand-50)] text-[var(--rt-brand-700)] dark:bg-[var(--rt-brand-500)]/15 dark:text-[var(--rt-brand-200)] font-medium'
                      : 'text-[var(--rt-ink-700)] dark:text-zinc-300 hover:bg-black/[.04] dark:hover:bg-white/5',
                  )}
                >
                  <Dot tone={statusToTone((d as any).status as DeviceStatus)} />
                  <span className="flex-1 min-w-0 truncate">{d.name || d.device_id}</span>
                  {isActive && <Check size={14} className="shrink-0" />}
                </button>
              );
            })}
          </div>
          <div className="h-px bg-[var(--rt-ink-200)] dark:bg-white/10 my-1" />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/admin/devices');
            }}
            className="w-full flex items-center gap-2.5 px-3 h-9 rounded-md text-left text-[12.5px] text-[var(--rt-ink-600)] dark:text-zinc-400 hover:bg-black/[.04] dark:hover:bg-white/5"
          >
            <Settings2 size={13} /> Spravovat zařízení
          </button>
        </div>
      )}
    </div>
  );
};

export default DevicePicker;
